import type { CSSProperties, ReactNode } from 'react';

import { SUMMARY_RAIL_COLS } from '../shared/layout';

// Loading placeholder for the checkout route, shown as the Suspense fallback while
// the lazy CheckoutPage chunk loads. Mirrors the real layout (left payment/coupon
// stack + right order-summary rail on desktop, single column + sticky bar on
// mobile) using CSS breakpoints, so it lands without a media-query flash. Borders
// are avoided (no `border` class) so the moveek-web host's global `.border` rule
// can't tint it.
const bar = (width: number | string, height: number): CSSProperties => ({
  width,
  height,
  borderRadius: 6,
  background: 'var(--color-surface-2)',
});

function Card({ children, style }: { children?: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ borderRadius: 20, background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm)', padding: 18, ...style }}>
      {children}
    </div>
  );
}

const divider: CSSProperties = { height: 1, background: 'var(--color-surface-2)', margin: '14px 0' };

export function CheckoutSkeleton() {
  return (
    <main aria-busy="true" aria-label="Đang tải trang thanh toán" className="mvk-root mvk-redcarpet flex min-h-dvh flex-col">
      <div className="mx-auto w-full max-w-[1080px] flex-1 px-4 pt-3.5 pb-6 lg:px-12 lg:pt-5 lg:pb-11">
        <div className="animate-pulse">
          {/* back link */}
          <span className="block" style={bar(110, 13)} />

          {/* stepper (desktop) / marquee (mobile) */}
          <div className="mt-3 lg:mt-[22px]">
            <span className="block" style={{ ...bar('100%', 48), maxWidth: 420 }} />
          </div>

          <div className={`mt-4 grid gap-4 lg:mt-[22px] ${SUMMARY_RAIL_COLS} lg:gap-[30px] lg:items-start`}>
            <section className="flex min-w-0 flex-col gap-4">
              {/* page title (desktop only) */}
              <div className="hidden lg:flex lg:flex-col lg:gap-2.5">
                <span style={bar(140, 12)} />
                <span style={bar(230, 28)} />
              </div>

              {/* express payment card */}
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <span style={bar('40%', 14)} />
                  <span style={bar('100%', 48)} />
                </div>
              </Card>

              {/* coupon card */}
              <Card>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <span style={bar('50%', 14)} />
                  <span style={bar('100%', 40)} />
                </div>
              </Card>

              {/* trust band */}
              <span style={{ ...bar(220, 16), alignSelf: 'center' }} />
            </section>

            {/* order-summary rail (desktop only) */}
            <div className="hidden lg:block">
              <Card>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ width: 52, height: 74, borderRadius: 10, background: 'var(--color-surface-2)', flexShrink: 0 }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <span style={bar('45%', 10)} />
                    <span style={bar('85%', 14)} />
                    <span style={bar('60%', 12)} />
                    <span style={bar('50%', 12)} />
                  </div>
                </div>
                <div style={divider} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={bar('40%', 12)} /><span style={bar(56, 12)} /></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={bar('35%', 12)} /><span style={bar(48, 12)} /></div>
                </div>
                <div style={divider} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={bar('30%', 14)} /><span style={bar(96, 26)} />
                </div>
                <span style={{ display: 'block', marginTop: 14, height: 54, borderRadius: 14, background: 'var(--color-surface-2)' }} />
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* sticky submit bar (mobile only) */}
      <div className="animate-pulse sticky inset-x-0 bottom-0 lg:hidden" style={{ background: 'var(--color-surface)', boxShadow: 'var(--shadow-pop)', padding: '12px 16px 16px' }}>
        <span className="block" style={{ height: 52, borderRadius: 14, background: 'var(--color-surface-2)' }} />
      </div>
    </main>
  );
}
